import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../interceptors";

const REC_TONE = {
  STRONG_YES: "bg-emerald-600 text-white",
  YES: "bg-emerald-100 text-emerald-700",
  NO: "bg-red-100 text-red-700",
  STRONG_NO: "bg-red-600 text-white",
};

const REC_LABEL = {
  STRONG_YES: "Strong yes",
  YES: "Yes",
  NO: "No",
  STRONG_NO: "Strong no",
};

export default function ScorecardsTab({ candidateId, jobId }) {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!candidateId) return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ candidateId });
    if (jobId) params.set("jobId", String(jobId));
    axiosInstance
      .get(`/job-posting/scorecards/by-candidate?${params.toString()}`)
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setError(e?.message || "Failed to load scorecards"))
      .finally(() => setLoading(false));
  }, [candidateId, jobId]);

  const submitHref = jobId
    ? `/admin/scorecard/${candidateId}/${jobId}`
    : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold">
            Interviewer scorecards
          </div>
          <div className="text-[11px] text-vf-muted mt-0.5">
            {items.length === 0 ? "No scorecards submitted yet." : `${items.length} submitted`}
          </div>
        </div>
        {submitHref && (
          <button
            onClick={() => nav(submitHref)}
            className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium"
          >
            + Submit scorecard
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-10 text-sm text-vf-muted">Loading…</div>
      ) : error ? (
        <div className="text-center py-10 text-sm text-red-700">{error}</div>
      ) : items.length === 0 ? (
        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl py-10 text-center text-sm text-vf-muted">
          No scorecards yet. Once interviewers submit, they appear here grouped by submitter.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((s) => (
            <SubmissionCard key={s.id} s={s} />
          ))}
        </div>
      )}
    </div>
  );
}

function SubmissionCard({ s }) {
  const ratings = useMemo(() => parseRatings(s.attributeRatings), [s.attributeRatings]);
  const submitted = s.submittedAt || s.createdAt;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="font-semibold text-vf-base">
            {s.submitterName || `Interviewer #${s.submittedBy}`}
          </div>
          <div className="text-[11px] text-vf-muted">
            {submitted ? new Date(submitted).toLocaleString() : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {s.overallRating != null && (
            <div className="text-right">
              <div className="text-2xl font-bold tabular-nums text-vf-base">{s.overallRating}</div>
              <div className="text-[10px] uppercase text-vf-muted font-semibold">overall</div>
            </div>
          )}
          {s.recommendation && (
            <span
              className={`px-2 py-1 rounded text-xs font-semibold ${REC_TONE[s.recommendation] || "bg-slate-100 text-slate-700"}`}
            >
              {REC_LABEL[s.recommendation] || s.recommendation}
            </span>
          )}
        </div>
      </div>

      {ratings.length > 0 && (
        <table className="w-full text-sm">
          <tbody>
            {ratings.map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="py-1.5 pr-3 text-vf-base">{r.attributeName || r.name || "—"}</td>
                <td className="py-1.5 text-right tabular-nums font-semibold text-vf-base w-12">
                  {r.rating ?? r.score ?? "—"}
                </td>
                <td className="py-1.5 pl-3 text-xs text-vf-muted">{r.notes || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(s.strengths || s.concerns) && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          {s.strengths && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-0.5">
                Strengths
              </div>
              <div className="text-xs text-vf-base leading-relaxed whitespace-pre-line">{s.strengths}</div>
            </div>
          )}
          {s.concerns && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-red-700 font-semibold mb-0.5">
                Concerns
              </div>
              <div className="text-xs text-vf-base leading-relaxed whitespace-pre-line">{s.concerns}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function parseRatings(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
